import { IconButton, Stack, TextField } from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

import type { ReactElement } from "react";

/** A single age-reduction band: reduce to `reductionPercent`% at `age`. */
export interface AgeReductionScheduleEntry {
  reductionPercent: number;
  age: number;
}

/** The draft (string-backed) form of a band while it is being edited. */
export interface DraftEntry {
  /** Stable key for React, since bands can be added and removed. */
  id: string;
  reductionPercent: string;
  age: string;
}

export const makeDraftEntry = (
  reductionPercent = "",
  age = "",
): DraftEntry => ({
  id: crypto.randomUUID(),
  reductionPercent,
  age,
});

export const toDraftEntries = (
  schedules: AgeReductionScheduleEntry[],
): DraftEntry[] =>
  schedules.length > 0
    ? schedules.map((s) =>
        makeDraftEntry(String(s.reductionPercent), String(s.age)),
      )
    : [makeDraftEntry()];

export const isValidEntry = (entry: DraftEntry): boolean => {
  const percent = Number(entry.reductionPercent);
  const age = Number(entry.age);

  return (
    entry.reductionPercent !== "" &&
    entry.age !== "" &&
    Number.isFinite(percent) &&
    Number.isInteger(age) &&
    percent >= 1 &&
    percent <= 100 &&
    age >= 1 &&
    age <= 120
  );
};

interface ScheduleBandsFieldProps {
  entries: DraftEntry[];
  showErrors: boolean;
  onUpdate: (index: number, field: keyof DraftEntry, value: string) => void;
  onRemove: (index: number) => void;
}

/**
 * Controlled list of age-reduction bands (`reduce to X% at age Y`) with
 * add/remove. Rendered by `AgeReductionScheduleCellEditor` when the
 * schedule-bearing type is selected.
 */
export const ScheduleBandsField = ({
  entries,
  showErrors,
  onUpdate,
  onRemove,
}: ScheduleBandsFieldProps): ReactElement => (
  <>
    {entries.map((entry, index) => {
      const invalid = showErrors && !isValidEntry(entry);

      return (
        <Stack key={entry.id} direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            type="number"
            label="Reduce to (%)"
            value={entry.reductionPercent}
            onChange={(e) =>
              onUpdate(index, "reductionPercent", e.target.value)
            }
            error={invalid}
            inputProps={{ min: 1, max: 100 }}
            fullWidth
          />

          <TextField
            size="small"
            type="number"
            label="At age"
            value={entry.age}
            onChange={(e) => onUpdate(index, "age", e.target.value)}
            error={invalid}
            inputProps={{ min: 1, max: 120, step: 1 }}
            fullWidth
          />

          <IconButton
            size="small"
            aria-label="Remove schedule entry"
            onClick={() => onRemove(index)}
            disabled={entries.length === 1}
          >
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>
      );
    })}
  </>
);
